import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';

import { DocumentList } from '../DocumentList';
import { DocumentCreationWizard } from '../DocumentCreationWizard';
import { CreateFromSourceModal } from '../CreateFromSourceModal';
import { ConfirmDialog } from '../DocumentModalList';
import { BulkConversionProgress } from '../BulkConversionProgress';
import { BulkResultsSummary } from '../BulkResultsSummary';
import { SmartMasterDocument } from './SmartMasterDocument';
import type { DocumentNavigationContext } from './SmartMasterDocument';
import { StateBadge } from '../StateBadge';
import { toast } from '../../Toast';
import { useMasterDocumentTranslation } from '../i18n';
import { classifyError } from './userFacingError';
import type { TranslateFn } from '../i18nFactory';
import { useBulkConversion } from './useBulkConversion';
import { createCrossingFetch } from './crossingFetch';
import { isFilterValueEmpty } from '../lookupFilters';
import {
  useMasterDocumentAdapter,
  useMasterDocumentTemplate,
  useIconRegistry,
  useUserPermissions,
} from '../provider/MasterDocumentProvider';
import { useMasterDocumentPrint } from '../printing/MasterDocumentPrintProvider';
import type { PrintContext } from '../printing/MasterDocumentPrinting.types';

import type {
  DocumentListColumn,
  DocumentListColumnFilter,
  DocumentListCompany,
  FilterPill,
  SearchField,
  WizardOption,
  CrossingClassOption,
  RowAction,
} from '../MasterDocument.types';
import type { DocumentListRequest, DocumentState } from '../adapter/MasterDocumentAdapter.types';
import type {
  DocumentListColumnTemplate,
  DocumentListFilterTemplate,
  DocumentListPillTemplate,
  SearcherFieldConfig,
} from '../template/MasterDocumentTemplate.types';

// ════════════════════════════════════════════════════════════
// PROPS
// ════════════════════════════════════════════════════════════

/**
 * Contexto de creación entregado a `onCreateDocument`.
 *
 * Coincide con los cuatro argumentos de `adapter.getNewDocument` /
 * `adapter.createDocument`, así que la ruta destino del host puede resolver el
 * estado inicial por su cuenta, sin datos adicionales y sin depender de estado
 * en memoria que un F5 destruiría.
 */
export interface MasterDocumentPageCreateContext {
  /**
   * Clase del documento ya resuelta: la elegida en el paso de clase del wizard cuando el
   * consumidor pasa `documentClasses`; si no, la prop `documentClass` o
   * `template.identity.documentClass`.
   */
  documentClass: string;
  /** Compañía elegida en el wizard. */
  companyId: string;
  /** Centro de operación elegido en el wizard. */
  operationCenterId: string;
  /** Tipo de documento elegido en el wizard. */
  documentTypeId: string;
}

export interface MasterDocumentPageProps {
  /** Clase del documento. Fallback: template.identity.documentClass */
  documentClass?: string;

  /**
   * Clases que esta superficie puede crear. Habilita el paso de CLASE en el
   * wizard, entre compañía y centro de operación.
   *
   * Solo tiene sentido cuando una misma página sirve a más de una clase (p.ej.
   * nota crédito de venta y nota crédito de venta de solo valor): la clase no se
   * puede derivar del tipo de documento —la relación es N:M— así que pasa a ser
   * un dato que captura el usuario. **Ausente ⇒ nada cambia**: la clase sigue
   * siendo la constante de `documentClass` / `template.identity.documentClass`.
   *
   * Se espera la lista ya filtrada por permisos de creación del usuario: el kit
   * no la filtra. Con una sola clase, la página la preselecciona.
   *
   * `documentClass` y `documentClasses` son alternativas, no complementos: con este
   * prop presente, `documentClass` queda inerte.
   *
   * ⚠️ Si la lista se resuelve de forma asíncrona, NO montes la página hasta tenerla.
   * Un array ausente o vacío equivale a "superficie de una sola clase", así que
   * durante la carga el wizard mostraría tres pasos y crearía el documento con la
   * clase pineada. El fallo es silencioso: nace un documento de la clase equivocada.
   *
   * Cuando está presente, la clase ELEGIDA es la que viaja al filtro de tipos de
   * documento, a `getNewDocument` y a `createDocument`; y el listado se pide para
   * TODAS las clases declaradas (`filters.documentClasses`), porque un visor
   * compartido lista los documentos de ambas.
   */
  documentClasses?: WizardOption[];

  /** Compañía del contexto del usuario (obligatorio) */
  companyId: string;

  /** Centro de operación del contexto del usuario (obligatorio) */
  operationCenterId: string;

  /** Si se pasa, arranca directo en el documento (edit/view). Si no, en el listado. */
  documentId?: string;

  // ── Delegación de navegación al host (opcional) ──────────
  //
  // Ausentes estos props, la página resuelve la navegación EN LÍNEA sobre su propia
  // ruta: comportamiento histórico, sin cambios para ningún consumidor actual.
  // Presente uno, la página delega ESE camino en el host y no cambia de vista, de
  // modo que una superficie pueda enrutar a su propia pantalla de detalle (p.ej.
  // para montar bloques propios junto al documento).
  //
  // La delegación es por camino e independiente: los props ausentes conservan su
  // comportamiento interno. Un host que delega el menú de fila pero no el clic de
  // fila obtiene navegación mixta — normalmente conviene delegar los tres juntos.

  /**
   * Clic sobre una fila del listado.
   * @default Abre el documento en línea, en modo edición.
   */
  onRowClick?: (row: Record<string, unknown>) => void;

  /**
   * Acción de fila «Ver detalle».
   * @default Abre el documento en línea, en modo solo lectura.
   */
  onViewRow?: (row: Record<string, unknown>) => void;

  /**
   * Acción de fila «Editar».
   * @default Abre el documento en línea, en modo edición.
   */
  onEditRow?: (row: Record<string, unknown>) => void;

  /**
   * URL del detalle de una fila, para que «Ver»/«Editar» se pinten como enlace y
   * el usuario recupere Ctrl/⌘+clic, clic con la rueda y «Abrir en pestaña nueva».
   * Ver `DocumentListProps.getRowHref`.
   *
   * **Sólo hace falta declararlo cuando el HOST gobierna la navegación** (pasa
   * `onEditRow`/`onViewRow` y navega con su router): ahí únicamente él conoce la
   * ruta. Constrúyela con el router —`router.buildLocation({...}).href` en
   * TanStack— y nunca concatenando cadenas, o en modo microfrontend el enlace
   * saldría sin el basepath y la pestaña nueva caería en una ruta inexistente.
   *
   * @default Cuando la navegación la resuelve esta página en línea, calcula el
   * `href` ella misma a partir de la URL actual (`?doc=<id>`, más `&mode=view`
   * para «Ver»), que es exactamente la URL que ya escribe al abrir el detalle y
   * la que restaura al montar.
   */
  getRowHref?: (row: Record<string, unknown>, action: 'view' | 'edit') => string | undefined;

  /**
   * Confirmación del wizard de creación.
   *
   * ⚠️ El documento AÚN NO EXISTE en el backend, así que no hay `documentId` que
   * entregar. Cuando se delega, `adapter.getNewDocument` NO se invoca: la ruta
   * destino resuelve el estado inicial con el contexto recibido, de modo que sea
   * recargable por URL.
   *
   * @default Resuelve el estado inicial vía `getNewDocument` y abre el documento nuevo en línea.
   */
  onCreateDocument?: (context: MasterDocumentPageCreateContext) => void;

  /**
   * Documento recién creado desde el modal «crear a partir de origen».
   *
   * A diferencia de `onCreateDocument`, aquí el documento YA EXISTE en el backend
   * y llega su id real.
   *
   * @default Abre el documento creado en línea, en modo edición.
   */
  onDocumentCreated?: (documentId: string) => void;

  /**
   * Navegación a OTRO documento existente pedida desde el detalle abierto en línea: un
   * nodo del grafo de trazabilidad, o el documento recién generado por un cruce.
   *
   * @default Intercepta el pseudo-key `navigate_document:<id>` en su adapter y abre el documento en línea.
   */
  onNavigateToDocument?: (documentId: string, context: DocumentNavigationContext) => void;
}

// ════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════

function buildColumns(
  templateCols: DocumentListColumnTemplate[],
  formatCurrency: (n: number) => string,
  searchers?: Record<string, SearcherFieldConfig>,
): DocumentListColumn[] {
  return templateCols.map((col) => {
    const base: DocumentListColumn = {
      key: col.key,
      dataKey: col.dataKey,
      header: col.header,
      align: col.align,
      mono: col.mono,
      width: col.width,
      ...(col.filter ? { filter: buildColumnFilter(col, searchers) } : {}),
    };
    if (col.badgeMap) {
      const map = col.badgeMap;
      base.render = (row) => {
        const val = String(row[col.key] ?? '');
        const badge = map[val];
        if (!badge) return val;
        return <StateBadge label={badge.label} bg={badge.bg} color={badge.color} />;
      };
    } else if (col.format === 'currency') {
      base.render = (row) => {
        const val = row[col.key];
        return typeof val === 'number' ? formatCurrency(val) : String(val ?? '');
      };
    }
    return base;
  });
}

/**
 * Filtro declarado en una columna → el que consume la grilla.
 *
 * Para una columna FORÁNEA (`type: 'lookup'`) resuelve la config del searcher:
 * `searcher` inline → `searchers[searcherKey]` → `searchers[field]` →
 * `searchers[key de la columna]`. Reusar el searcher que el template ya declara
 * para el campo equivalente del detalle es el caso normal. Sin config que
 * resolver, el embudo degrada al input de texto.
 */
function buildColumnFilter(
  col: DocumentListColumnTemplate,
  searchers?: Record<string, SearcherFieldConfig>,
): DocumentListColumnFilter {
  const f = col.filter!;
  const searcher = f.type === 'lookup'
    ? f.searcher ?? searchers?.[f.searcherKey ?? ''] ?? (f.field ? searchers?.[f.field] : undefined) ?? searchers?.[col.key]
    : undefined;
  return {
    type: f.type,
    field: f.field,
    options: f.options as DocumentListColumnFilter['options'],
    placeholder: f.placeholder,
    ...(searcher ? { searcher: { ...searcher } } : {}),
  };
}

function buildPills(pills: DocumentListPillTemplate[], activeKey: string): FilterPill[] {
  return pills.map((p) => ({
    key: p.key,
    label: p.label,
    bg: p.bg,
    color: p.color,
    active: p.key === activeKey,
  }));
}

function buildSearchFields(filters: DocumentListFilterTemplate[]): SearchField[] {
  return filters.map((f) => ({
    key: f.key,
    label: f.label,
    type: f.type,
    options: f.options as SearchField['options'],
    placeholder: f.placeholder,
  }));
}

// ════════════════════════════════════════════════════════════
// GLOBAL COMPANY SENTINEL
// ════════════════════════════════════════════════════════════

/**
 * Centinela "todas las compañías" del selector del listado.
 *
 * Es una función y no una constante porque su `name` es texto visible: se resuelve
 * con el traductor en el idioma activo al montar. (La etiqueta ya seleccionada no
 * se re-traduce si el usuario cambia de idioma en caliente, porque vive en estado
 * de selección; el resto del listado sí.)
 */
const globalCompany = (t: TranslateFn): DocumentListCompany => ({
  uuid: '000',
  code: '000',
  name: t('master_document.list.allCompanies'),
});

// ════════════════════════════════════════════════════════════
// VIEWS
// ════════════════════════════════════════════════════════════

/**
 * Nombre del parámetro de URL que recuerda qué documento está abierto.
 *
 * El detalle se abre EN LÍNEA —la página no cambia de ruta— así que hasta ahora
 * vivía sólo en el estado de React: al refrescar (F5) el navegador volvía a montar
 * el componente en `{ kind: 'list' }` y el usuario aterrizaba en el listado,
 * perdiendo de vista el documento que estaba editando. Reflejarlo en la barra de
 * direcciones lo arregla y, de paso, hace el detalle enlazable y compartible.
 *
 * Se escribe con `replaceState` y NO con `pushState`: la página vive dentro del
 * router del host (TanStack Router en los MFE de comercial) y empujar entradas de
 * historial a su espalda le desincroniza la pila. `replaceState` sobre la MISMA
 * ruta sólo cambia la query, que es lo que el refresco necesita leer.
 */
const PARAM_DOCUMENTO = 'doc';
/** `?mode=view` marca que el documento se abrió en sólo lectura. */
const PARAM_MODO = 'mode';

/** Lee de la URL el documento que hay que restaurar al montar (o tras un `popstate`). */
function leerVistaDeLaUrl(): { documentId: string; viewOnly: boolean } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const id = params.get(PARAM_DOCUMENTO);
  if (!id) return null;
  return { documentId: id, viewOnly: params.get(PARAM_MODO) === 'view' };
}

type View =
  | { kind: 'list' }
  | { kind: 'wizard' }
  | { kind: 'document'; documentId: string; viewOnly?: boolean }
  | { kind: 'new'; initialState: import('../adapter/MasterDocumentAdapter.types').DocumentState }
  | { kind: 'create'; documentTypeId: string };

// ════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════

/**
 * MasterDocumentPage — Punto de entrada único por clase de documento.
 *
 * Recibe la compañía y centro de operación del contexto del usuario.
 * El wizard solo pide el tipo de documento — compañía y CO ya están seteados.
 *
 * @example
 * ```tsx
 * <MasterDocumentProvider template={pedidoVentaTemplate} adapter={adapter}>
 *   <MasterDocumentPage
 *     companyId="C001"
 *     operationCenterId="CO001"
 *   />
 * </MasterDocumentProvider>
 * ```
 *
 * @example Delegando la navegación a las rutas del host
 * ```tsx
 * // La página no abre el documento en línea: enruta a las pantallas propias del
 * // host, donde éste puede montar bloques que el kit no conoce.
 * <MasterDocumentPage
 *   companyId="C001"
 *   operationCenterId="CO001"
 *   // String(row.id): la fila es Record<string, unknown> y TS no interpola `unknown`.
 *   onRowClick={(row) => navigate(`/notas-credito/${String(row.id)}/edit`)}
 *   onViewRow={(row) => navigate(`/notas-credito/${String(row.id)}`)}
 *   onEditRow={(row) => navigate(`/notas-credito/${String(row.id)}/edit`)}
 *   onCreateDocument={(ctx) => navigate(`/notas-credito/nuevo?tipo=${ctx.documentTypeId}`)}
 *   onDocumentCreated={(id) => navigate(`/notas-credito/${id}/edit`)}
 *   onNavigateToDocument={(id, { reason }) =>
 *     navigate(reason === 'traceability' ? `/notas-credito/${id}` : `/notas-credito/${id}/edit`)}
 * />
 * ```
 */
export function MasterDocumentPage({
  documentClass,
  documentClasses,
  companyId,
  operationCenterId,
  documentId,
  onRowClick,
  onViewRow,
  onEditRow,
  getRowHref,
  onCreateDocument,
  onDocumentCreated,
  onNavigateToDocument,
}: MasterDocumentPageProps) {
  const { t } = useMasterDocumentTranslation();
  const adapter = useMasterDocumentAdapter();
  const template = useMasterDocumentTemplate();
  const iconRegistry = useIconRegistry();
  const print = useMasterDocumentPrint();
  const userPermissions = useUserPermissions();

  const docClass = documentClass ?? template.identity.documentClass;
  const listConfig = template.list;
  const wizardConfig = template.wizard;

  // ── Paso de clase en el wizard (opt-in) ─────────────────
  // `documentClasses` suele llegar como literal, con identidad nueva en cada
  // render. Todo lo que alimente efectos o `useCallback` se deriva de una CLAVE
  // primitiva estable: si no, `fetchList` cambiaría de identidad en cada render y
  // el efecto del listado volvería a pedir la página sin parar.
  const documentClassIdsKey = JSON.stringify(documentClasses?.map((dc) => dc.id) ?? []);
  const documentClassIds = useMemo(
    () => JSON.parse(documentClassIdsKey) as string[],
    [documentClassIdsKey],
  );
  const hasClassStep = documentClassIds.length > 0;

  // Con una sola clase permitida, la página la preselecciona: el estado del wizard
  // es suyo, así que el consumidor no puede preseleccionarla desde fuera. Un
  // `select` de una opción ya se lee como informativo.
  const singleClassId = documentClassIds.length === 1 ? documentClassIds[0] : undefined;

  // Índices POSICIONALES de los pasos del wizard (ver DocumentCreationWizardProps):
  // con el paso de clase activo, la clase toma el 2 y corre CO al 3 y tipo al 4.
  const coStepIndex = hasClassStep ? 3 : 2;
  const typeStepIndex = hasClassStep ? 4 : 3;

  // ── Create-from-source (caso A): ¿el listado abre el selector de origen? ──
  // Activo cuando el template declara `createFromSource`, el adapter implementa
  // `createFromSource` y existe una acción `actionType:'createFromSource'` en modo
  // `create` cuyo permiso el usuario tiene. Entonces "+ Nuevo" abre el modal en
  // lugar del wizard de documento en blanco.
  const createFromSourceEnabled = useMemo(() => {
    if (!template.createFromSource || !adapter.createFromSource) return false;
    const registry = template.actionRegistry ?? {};
    return Object.values(registry).some((entry) => {
      if (entry.actionType !== 'createFromSource' || entry.createFromSource?.mode !== 'create') return false;
      if (entry.permissionVisibility && userPermissions !== null && !userPermissions.includes(entry.permissionVisibility)) return false;
      return true;
    });
  }, [template.createFromSource, template.actionRegistry, adapter, userPermissions]);

  const [createFromSourceOpen, setCreateFromSourceOpen] = useState(false);

  // ── ¿El listado ofrece la acción de crear? ───────────────
  // `list.createPermission` gatea el afordance de crear completo — tanto el wizard
  // de documento en blanco como el flujo `createFromSource`, porque lo que se gatea
  // es "crear", no la ruta que toma. Sin permiso el botón queda AUSENTE, no
  // deshabilitado: una acción que el usuario no puede ejecutar no se ofrece.
  //
  // Convención de permisos del kit: `userPermissions === null` significa que no hay
  // sistema de permisos activo, y el fallback es permisivo (mismo criterio que
  // `permissionVisibility` arriba). Así, un consumidor que no pasa `userPermissions`
  // al provider no pierde su botón de crear.
  //
  // `list.canCreate === false` es una condición distinta y previa: esa clase no se
  // crea a mano en absoluto —nace de un proceso de generación—, así que no hay
  // permiso que declarar y ningún permiso la revierte.
  const canCreate = useMemo(() => {
    if (listConfig?.canCreate === false) return false;
    const required = listConfig?.createPermission;
    if (!required) return true;
    if (userPermissions === null) return true;
    return userPermissions.includes(required);
  }, [listConfig?.canCreate, listConfig?.createPermission, userPermissions]);

  // ── View state ──────────────────────────────────────────
  // El `documentId` del host manda; si no lo hay, se restaura lo que diga la URL,
  // que es lo que sobrevive a un refresco.
  const [view, setView] = useState<View>(() => {
    if (documentId) return { kind: 'document', documentId };
    const deLaUrl = leerVistaDeLaUrl();
    return deLaUrl
      ? { kind: 'document', documentId: deLaUrl.documentId, viewOnly: deLaUrl.viewOnly }
      : { kind: 'list' };
  });

  useEffect(() => {
    if (documentId) setView({ kind: 'document', documentId });
  }, [documentId]);

  // Refleja la vista en la URL. Sólo el detalle: un asistente de creación a medias
  // no se restaura —volver a un formulario en construcción con los datos perdidos
  // desorienta más que aterrizar en el listado.
  //
  // No se toca la URL cuando el host gobierna la navegación (`documentId` por prop):
  // ahí la ruta ya identifica al documento y escribir encima duplicaría el dato.
  useEffect(() => {
    if (typeof window === 'undefined' || documentId) return;
    const url = new URL(window.location.href);
    const actual = url.searchParams.get(PARAM_DOCUMENTO);
    if (view.kind === 'document') {
      url.searchParams.set(PARAM_DOCUMENTO, view.documentId);
      if (view.viewOnly) url.searchParams.set(PARAM_MODO, 'view');
      else url.searchParams.delete(PARAM_MODO);
    } else if (actual === null) {
      return; // nada que limpiar: evita un `replaceState` por cada render del listado
    } else {
      url.searchParams.delete(PARAM_DOCUMENTO);
      url.searchParams.delete(PARAM_MODO);
    }
    const destino = `${url.pathname}${url.search}${url.hash}`;
    if (destino !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, '', destino);
    }
  }, [view, documentId]);

  // Si el usuario usa atrás/adelante del navegador, la URL cambia sin que React se
  // entere: sin esto la barra de direcciones y la pantalla dirían cosas distintas.
  useEffect(() => {
    if (typeof window === 'undefined' || documentId) return;
    const alNavegar = () => {
      const deLaUrl = leerVistaDeLaUrl();
      setView(deLaUrl
        ? { kind: 'document', documentId: deLaUrl.documentId, viewOnly: deLaUrl.viewOnly }
        : { kind: 'list' });
    };
    window.addEventListener('popstate', alNavegar);
    return () => window.removeEventListener('popstate', alNavegar);
  }, [documentId]);

  // ── List state ──────────────────────────────────────────
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [activePill, setActivePill] = useState('all');
  const [searchValues, setSearchValues] = useState<Record<string, unknown>>({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | undefined>();

  // ── Company selector del listado ────────────────────────
  const [availableCompanies, setAvailableCompanies] = useState<DocumentListCompany[]>([]);
  const [selectedListCompany, setSelectedListCompany] = useState<DocumentListCompany>(() => globalCompany(t));

  // El paginador del canon ofrece selector de «registros por página», así que el
  // tamaño deja de ser una constante de la plantilla y pasa a ser estado. La
  // plantilla sigue decidiendo el valor INICIAL.
  const [pageSize, setPageSize] = useState(listConfig?.defaultPageSize ?? 10);

  // Búsqueda básica (opt-in por plantilla, ver `DocumentListTemplateConfig.quickSearch`).
  const quickSearchCfg = listConfig?.quickSearch;
  const quickSearchEnabled = !!quickSearchCfg && quickSearchCfg.enabled !== false;
  const [quickSearch, setQuickSearch] = useState('');
  // Lo que se MANDA al servidor va con debounce: sin él, filtrar sería una
  // petición por tecla. 350ms es el mismo retardo que usan los embudos de
  // columna, aquí y en MasterCrud.
  const [quickSearchApplied, setQuickSearchApplied] = useState('');
  useEffect(() => {
    if (!quickSearchEnabled) return;
    const id = window.setTimeout(() => setQuickSearchApplied(quickSearch.trim()), 350);
    return () => window.clearTimeout(id);
  }, [quickSearch, quickSearchEnabled]);

  // ── Wizard state ────────────────────────────────────────
  const [companies, setCompanies] = useState<WizardOption[]>([]);
  const [operationCenters, setOperationCenters] = useState<WizardOption[]>([]);
  const [documentTypes, setDocumentTypes] = useState<WizardOption[]>([]);
  const [selectedType, setSelectedType] = useState<string>();
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [cosLoading, setCosLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Filas con una acción destructiva en vuelo (P3 de LOADING-INTEGRATION.md).
  // Es un conjunto y no un solo id porque `runRowAction` no serializa: nada
  // impide disparar "anular" en una fila y "eliminar" en otra antes de que la
  // primera responda, y con un único id la primera perdería su indicador.
  const [busyRowIds, setBusyRowIds] = useState<string[]>([]);

  const markRowBusy = useCallback((id: string, busy: boolean) => {
    setBusyRowIds((prev) =>
      busy ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id),
    );
  }, []);

  // Cache SWR: evita flash de loading cuando los datos ya fueron fetched antes
  const wizardCacheRef = useRef<Map<string, WizardOption[]>>(new Map());

  // Cancela respuestas en vuelo cuando el paso 1 o 2 cambia de selección
  const latestCOKeyRef = useRef('');
  const latestTypesKeyRef = useRef('');

  // Selecciones locales del wizard (arrancan con el contexto, el usuario puede sobreescribir)
  const [wizardCompanyId, setWizardCompanyId] = useState(companyId);
  const [wizardCOId, setWizardCOId] = useState(operationCenterId);
  // Clase elegida en el wizard. Solo se puebla cuando hay paso de clase; sin él
  // queda `undefined` para siempre y `effectiveDocClass` es la clase de siempre.
  const [wizardDocClass, setWizardDocClass] = useState<string | undefined>(singleClassId);

  // Clase que gobierna la CREACIÓN: la elegida cuando la superficie sirve a varias
  // clases, la pineada en cualquier otro caso. Un solo símbolo para no dejar
  // ningún call site leyendo la constante por descuido.
  const effectiveDocClass = wizardDocClass || docClass;

  // ── Bulk conversion state ───────────────────────────────
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [bulkConvertOptions, setBulkConvertOptions] = useState<CrossingClassOption[]>([]);

  // ── Bulk conversion ─────────────────────────────────────
  const baseUrl = (adapter as any).baseUrl ?? 'http://localhost:5204'; // eslint-disable-line @typescript-eslint/no-explicit-any
  const crossingFetch = useMemo(() => createCrossingFetch(adapter, baseUrl), [adapter, baseUrl]);
  const {
    job: bulkJob,
    showResults: showBulkResults,
    startJob: startBulkJob,
    cancelJob: cancelBulkJob,
    closeProgress: closeBulkProgress,
    openResults: openBulkResults,
    closeResults: closeBulkResults,
    retryFailed: retryBulkFailed,
  } = useBulkConversion({ crossingFetch });

  useEffect(() => {
    if (!template.crossing?.enabled) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    crossingFetch<any>('GET', '/api/v1/crossing/rules')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((payload: any) => {
        const rules: any[] = Array.isArray(payload) ? payload : (payload?.data ?? []); // eslint-disable-line @typescript-eslint/no-explicit-any
        setBulkConvertOptions(rules.map((rule: any) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
          id: rule.child_class_id,
          label: rule.child_class_label,
          description: rule.description,
        })));
      })
      .catch(() => {});
  }, [template.crossing?.enabled, crossingFetch]);

  // ── Fetch companies for list selector ──────────────────
  useEffect(() => {
    if (!adapter.listCompanies) return;
    adapter.listCompanies()
      .then((fetchedCompanies) => {
        setAvailableCompanies(fetchedCompanies);
        const match = fetchedCompanies.find((c) => String(c.uuid) === String(companyId));
        if (match) {
          setSelectedListCompany(match);
        } else if (fetchedCompanies.length > 0) {
          setSelectedListCompany(fetchedCompanies[0]);
        }
      })
      .catch(() => { /* no-op */ });
  }, [adapter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch list ──────────────────────────────────────────
  // Filtros del embudo de cada columna, indexados por el campo que filtran.
  // Van aparte de los del panel: son dos afordances distintos y "Limpiar" (del
  // panel) no debe borrar en silencio un embudo que el usuario dejó activo.
  const [columnFilters, setColumnFilters] = useState<Record<string, unknown>>({});

  // Cambio de un embudo: recarga desde la página 1. La recarga la dispara el
  // efecto del listado, porque `columnFilters` entra en las deps de `fetchList`
  // (el control de texto ya viene con debounce, así que no hay una petición por
  // tecla). Un valor vacío BORRA la clave en vez de mandar `{ campo: '' }`.
  const handleColumnFilterChange = useCallback((field: string, value: unknown) => {
    setColumnFilters((prev) => {
      if (!isFilterValueEmpty(value)) return { ...prev, [field]: value };
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setPage(1);
  }, []);

  const fetchList = useCallback(async (
    pageNum: number,
    pillKey: string,
    filters: Record<string, unknown>,
    companyFilter: DocumentListCompany = selectedListCompany,
  ) => {
    if (!adapter.listDocuments) return;
    setListLoading(true);
    setListError(undefined);
    try {
      const pillConfig = Array.isArray(listConfig?.pills) ? listConfig.pills.find((p) => p.key === pillKey) : undefined;
      const isGlobal = String(companyFilter.uuid) === '000';
      const request: DocumentListRequest = {
        page: pageNum,
        pageSize,
        companyId: isGlobal ? undefined : String(companyFilter.uuid),
        stateFilter: pillConfig?.stateFilter ?? undefined,
        // Búsqueda básica. Sólo viaja si la plantilla la declaró: en una
        // superficie sin `quickSearch` la caja no existe y el valor es ''.
        search: quickSearchApplied || undefined,
        filters: {
          ...filters,
          // El embudo de la columna es el control más inmediato: si coincide con
          // un filtro del panel sobre el mismo campo, manda el de la columna.
          ...columnFilters,
          // Un visor compartido lista los documentos de TODAS sus clases, no de la
          // que esté elegida en el wizard. Se manda `documentClasses` y se OMITE
          // `documentClass`: enviar las dos sería un request que se contradice a sí
          // mismo, y obligaría al adapter a decidir cuál gana.
          ...(hasClassStep
            ? { documentClasses: documentClassIds }
            : { documentClass: docClass }),
          operationCenterId,
        },
      };
      const response = await adapter.listDocuments(request);
      setRows(response.rows);
      setTotalCount(response.totalCount);
    } catch (err) {
      setListError(String(err));
    } finally {
      setListLoading(false);
    }
  }, [adapter, listConfig, pageSize, docClass, hasClassStep, documentClassIds, selectedListCompany, operationCenterId, columnFilters, quickSearchApplied]);

  useEffect(() => {
    if (view.kind === 'list') {
      fetchList(page, activePill, searchValues);
    }
  }, [view.kind, page, activePill, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Wizard SWR helper ───────────────────────────────────
  // Muestra datos cacheados inmediatamente (sin loading) y revalida en background.
  // Solo muestra skeleton en el primer fetch real (sin cache).
  // latestKeyRef: si la clave ya no coincide cuando llega la respuesta, la descarta
  // (previene que un fetch en vuelo pise datos de una selección posterior).
  const fetchWizardStep = useCallback(async (
    entity: string,
    request: Record<string, unknown>,
    setter: (opts: WizardOption[]) => void,
    mapper: (item: Record<string, unknown>) => WizardOption,
    setLoading: (v: boolean) => void,
    latestKeyRef?: React.MutableRefObject<string>,
  ) => {
    const key = `${entity}:${JSON.stringify(request)}`;
    if (latestKeyRef) latestKeyRef.current = key;

    const cached = wizardCacheRef.current.get(key);
    if (cached) {
      setter(cached);
    } else {
      setLoading(true);
    }
    try {
      const res = await adapter.fetcher(entity, request as never);
      if (latestKeyRef && latestKeyRef.current !== key) return;
      const opts = (res.data as Record<string, unknown>[]).map(mapper);
      wizardCacheRef.current.set(key, opts);
      setter(opts);
    } catch { /* no-op */ } finally {
      // Se limpia el flag de carga en dos casos: cuando esta petición sigue siendo
      // la vigente, y cuando ya NADIE está pidiendo (clave invalidada a '').
      //
      // Ese segundo caso era un skeleton eterno: al cancelar un paso sin lanzar otra
      // petición —cambiar de clase o de compañía mientras los tipos cargaban, o
      // reabrir el wizard con un fetch en vuelo— la respuesta descartada salía por
      // el `return` de arriba y nadie apagaba el flag.
      //
      // Cuando la clave es OTRA petición en vuelo, el flag es suyo y lo apaga ella:
      // apagarlo aquí haría desaparecer el skeleton mientras esa sigue cargando.
      const idle = latestKeyRef?.current === '';
      if (!latestKeyRef || latestKeyRef.current === key || idle) setLoading(false);
    }
  }, [adapter]);

  // ── Wizard: sincroniza selecciones + carga datos cuando se abre ──
  // El filtrado de los pasos dependientes (CO y tipo de documento) depende
  // EXCLUSIVAMENTE del estado interno del wizard (wizardCompanyId/wizardCOId),
  // nunca del listado/contexto. La única conveniencia es pre-seleccionar la
  // compañía + CO del contexto cuando el listado está en una compañía específica;
  // en "Global" (uuid='000') todo arranca vacío y deshabilitado.
  useEffect(() => {
    if (view.kind !== 'wizard') return;

    const isGlobal = String(selectedListCompany.uuid) === '000';
    const initialCompanyId = isGlobal ? '' : String(selectedListCompany.uuid);
    // Con paso de clase, la clase arranca elegida solo si hay una sola.
    const initialClass = hasClassStep ? singleClassId : undefined;
    // El CO solo se pre-selecciona junto a una compañía específica. Sin compañía
    // (caso Global) no se pre-llena para evitar un estado inconsistente.
    //
    // Tampoco se pre-llena mientras falte la clase: elegirla limpia el CO (la
    // clase acota los tipos), así que mostrarlo relleno-y-deshabilitado solo para
    // vaciarlo en el primer clic sería peor que dejarlo vacío.
    const initialCOId = initialCompanyId && (!hasClassStep || initialClass) ? operationCenterId : '';
    // Clase que filtra los tipos en esta apertura.
    const initialDocClass = initialClass || docClass;

    setWizardCompanyId(initialCompanyId);
    setWizardCOId(initialCOId);
    setWizardDocClass(initialClass);
    setSelectedType(undefined);

    // Los pasos dependientes arrancan vacíos; solo se llenan vía los fetches de
    // abajo cuando hay selección en el wizard. Invalida fetches en vuelo de una
    // apertura previa para evitar condiciones de carrera.
    setOperationCenters([]);
    setDocumentTypes([]);
    latestCOKeyRef.current = '';
    latestTypesKeyRef.current = '';

    if (!wizardConfig) return;

    const companyStep = wizardConfig.steps.find((s) => s.key === 'company');
    const coStep      = wizardConfig.steps.find((s) => s.key === 'co');
    const typeStep    = wizardConfig.steps.find((s) => s.key === 'docType')
      ?? wizardConfig.steps[wizardConfig.steps.length - 1];

    // La lista de compañías es independiente: siempre se carga.
    if (companyStep) {
      void fetchWizardStep(
        companyStep.entity,
        { page: 1, pageSize: 100, fields: [] },
        setCompanies,
        (item) => ({ id: String(item.Id ?? item.id ?? ''), label: String(item.name ?? item.nombre ?? item.label ?? '') }),
        setCompaniesLoading,
      );
    }

    // CObs: solo si hay compañía pre-seleccionada en el wizard, filtrando por ella.
    if (coStep && initialCompanyId) {
      void fetchWizardStep(
        coStep.entity,
        { page: 1, pageSize: 100, fields: [], filters: { companyId: initialCompanyId } },
        setOperationCenters,
        (item) => ({ id: String(item.Id ?? item.id ?? ''), label: [item.code, item.name].filter(Boolean).join(' — ') }),
        setCosLoading,
        latestCOKeyRef,
      );
    }

    // Tipos: solo si hay compañía + CO pre-seleccionados en el wizard, filtrando
    // por AMBOS valores del wizard (nunca por el contexto/listado).
    if (typeStep && initialCompanyId && initialCOId) {
      void fetchWizardStep(
        typeStep.entity,
        { page: 1, pageSize: 50, fields: [], filters: { companyId: initialCompanyId, operationCenterId: initialCOId, documentClass: initialDocClass } },
        setDocumentTypes,
        (item) => ({ id: String(item.Id ?? item.id ?? ''), label: String(item.name ?? item.nombre ?? item.label ?? ''), description: String(item.description ?? '') }),
        setTypesLoading,
        latestTypesKeyRef,
      );
    }
  }, [view.kind, wizardConfig, fetchWizardStep, selectedListCompany, operationCenterId, docClass, hasClassStep, singleClassId]);

  // ── Wizard: handler de cambio de compañía ──
  const handleWizardCompanyChange = useCallback((id: string) => {
    setWizardCompanyId(id);
    setWizardCOId('');
    setSelectedType(undefined);
    setOperationCenters([]);
    setDocumentTypes([]);
    // Invalida cualquier fetch en vuelo de pasos dependientes
    latestCOKeyRef.current = '';
    latestTypesKeyRef.current = '';
    if (!wizardConfig) return;
    const coStep = wizardConfig.steps.find((s) => s.key === 'co');
    if (!coStep) return;
    void fetchWizardStep(
      coStep.entity,
      { page: 1, pageSize: 100, fields: [], filters: { companyId: id } },
      setOperationCenters,
      (item) => ({ id: String(item.Id ?? item.id ?? ''), label: [item.code, item.name].filter(Boolean).join(' — ') }),
      setCosLoading,
      latestCOKeyRef,
    );
  }, [fetchWizardStep, wizardConfig]);

  // ── Wizard: handler de cambio de clase de documento ──
  // La clase acota la lista de tipos, así que al cambiarla se limpian los pasos de
  // abajo igual que ya se limpian al cambiar de compañía. Los centros de operación
  // NO se recargan ni se vacían: dependen de la compañía, no de la clase.
  const handleWizardClassChange = useCallback((id: string) => {
    setWizardDocClass(id);
    setWizardCOId('');
    setSelectedType(undefined);
    setDocumentTypes([]);
    latestTypesKeyRef.current = '';
  }, []);

  // ── Wizard: handler de cambio de centro de operación ──
  const handleWizardCOChange = useCallback((id: string) => {
    setWizardCOId(id);
    setSelectedType(undefined);
    setDocumentTypes([]);
    latestTypesKeyRef.current = '';
    if (!wizardConfig) return;
    const typeStep = wizardConfig.steps.find((s) => s.key === 'docType')
      ?? wizardConfig.steps[wizardConfig.steps.length - 1];
    if (!typeStep) return;
    void fetchWizardStep(
      typeStep.entity,
      { page: 1, pageSize: 50, fields: [], filters: { companyId: wizardCompanyId, operationCenterId: id, documentClass: effectiveDocClass } },
      setDocumentTypes,
      (item) => ({ id: String(item.Id ?? item.id ?? ''), label: String(item.name ?? item.nombre ?? item.label ?? ''), description: String(item.description ?? '') }),
      setTypesLoading,
      latestTypesKeyRef,
    );
  }, [fetchWizardStep, wizardConfig, wizardCompanyId, effectiveDocClass]);

  // ── Wizard confirm ──────────────────────────────────────
  // Llama a getNewDocument para traer el template pre-poblado del backend
  // sin persistir. El POST real ocurre solo al guardar por primera vez.
  const handleWizardConfirm = useCallback(async () => {
    if (!selectedType) return;
    // Con paso de clase, sin clase elegida no se crea nada. El botón ya está
    // deshabilitado, pero la invariante se defiende aquí también: caer al
    // `docClass` del template crearía el documento con la clase equivocada, que es
    // justo el problema que este paso resuelve.
    if (hasClassStep && !wizardDocClass) return;
    const typeId = selectedType;
    setSelectedType(undefined);

    // Delegación: el host enruta a su propia pantalla de creación. `getNewDocument`
    // NO se invoca aquí — la ruta destino lo resuelve con el contexto entregado, para
    // que sea recargable por URL en lugar de depender de estado en memoria.
    // Se vuelve al listado para cerrar el wizard: si la navegación del host no
    // desmonta esta página, el usuario no queda atrapado en el modal.
    //
    // La clase que viaja es `effectiveDocClass`, no `docClass`: con paso de clase en el
    // wizard es la que eligió el usuario. Entregar la del template crearía el documento
    // con la clase equivocada, que es justo lo que ese paso resuelve.
    if (onCreateDocument) {
      onCreateDocument({
        documentClass: effectiveDocClass,
        companyId: wizardCompanyId,
        operationCenterId: wizardCOId,
        documentTypeId: typeId,
      });
      setView({ kind: 'list' });
      return;
    }

    if (adapter.getNewDocument) {
      setConfirmLoading(true);
      try {
        const initialState = await adapter.getNewDocument(
          effectiveDocClass, wizardCompanyId, wizardCOId, typeId,
        );
        // La clase viaja en el estado del documento para que el POST del primer
        // guardado (`createDocument`, en el reducer) y las consultas por clase del
        // detalle usen ESTA clase y no la constante del template. Sin paso de
        // clase el valor es justo esa constante, así que nada cambia.
        setView({ kind: 'new', initialState: { ...initialState, documentClass: effectiveDocClass } });
      } catch {
        setView({ kind: 'create', documentTypeId: typeId });
      } finally {
        setConfirmLoading(false);
      }
    } else {
      setView({ kind: 'create', documentTypeId: typeId });
    }
  }, [selectedType, adapter, effectiveDocClass, hasClassStep, wizardDocClass, wizardCompanyId, wizardCOId, onCreateDocument]);

  // ── Build list props ────────────────────────────────────
  const columns = useMemo(
    () => Array.isArray(listConfig?.columns)
      ? buildColumns(listConfig!.columns, adapter.formatCurrency, template.searchers)
      : [],
    [listConfig?.columns, adapter.formatCurrency, template.searchers],
  );
  const pills = useMemo(
    () => Array.isArray(listConfig?.pills) ? buildPills(listConfig!.pills, activePill) : [],
    [listConfig?.pills, activePill],
  );
  const searchFields = useMemo(
    () => Array.isArray(listConfig?.filters) ? buildSearchFields(listConfig!.filters) : [],
    [listConfig?.filters],
  );

  // ── Company change in list ──────────────────────────────
  const handleListCompanyChange = useCallback((company: DocumentListCompany) => {
    setSelectedListCompany(company);
    setPage(1);
    fetchList(1, activePill, searchValues, company);
  }, [fetchList, activePill, searchValues]);

  // ── Navigation ──────────────────────────────────────────
  // Cada camino delega en el host si recibió su callback; si no, resuelve en línea
  // como siempre. El `return` temprano es lo que garantiza que la página NO cambie
  // de vista cuando delega: el host es dueño de la navegación desde ese punto.
  const handleRowClick = useCallback((row: Record<string, unknown>) => {
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    const id = String(row.id ?? row.documentId ?? '');
    if (id) setView({ kind: 'document', documentId: id });
  }, [onRowClick]);

  const handleViewRow = useCallback((row: Record<string, unknown>) => {
    if (onViewRow) {
      onViewRow(row);
      return;
    }
    const id = String(row.id ?? row.documentId ?? '');
    if (id) setView({ kind: 'document', documentId: id, viewOnly: true });
  }, [onViewRow]);

  const handleEditRow = useCallback((row: Record<string, unknown>) => {
    if (onEditRow) {
      onEditRow(row);
      return;
    }
    const id = String(row.id ?? row.documentId ?? '');
    if (id) setView({ kind: 'document', documentId: id, viewOnly: false });
  }, [onEditRow]);

  /**
   * `href` de «Ver»/«Editar», que es lo que convierte esas acciones en enlaces y
   * devuelve al usuario Ctrl/⌘+clic y clic con la rueda para abrir el detalle en
   * otra pestaña (ver `DocumentListProps.getRowHref`).
   *
   * El del host manda siempre. Cuando no lo hay, esta página lo resuelve SÓLO
   * para los caminos que ella misma gobierna, y ese matiz es lo importante:
   *
   * - Si el host delegó esa acción concreta (`onEditRow` / `onViewRow`), la
   *   navegación es suya y `?doc=<id>` NO es su destino. Enlazar ahí abriría en la
   *   pestaña nueva el detalle EN LÍNEA en vez de la ruta del host —que en varios
   *   módulos es la única superficie que monta sus bloques propios—, así que la
   *   pestaña nueva mostraría algo distinto del clic normal. Sin `href`: la acción
   *   sigue siendo un botón y el host puede declarar el suyo cuando quiera el
   *   enlace.
   * - Si el host gobierna por `documentId`, esta página no escribe la URL (ver el
   *   efecto de sincronización), así que tampoco puede construirla.
   *
   * La URL se calcula sobre `window.location`, que es la MISMA que el efecto de
   * sincronización escribe al abrir el detalle y la que `leerVistaDeLaUrl()`
   * restaura al montar: la pestaña nueva aterriza exactamente donde aterriza un
   * refresco. Conserva los demás parámetros de la lista (filtros, página), que el
   * detalle ignora y que quedan ahí para cuando esa pestaña vuelva al listado.
   */
  const resolverHrefDeFila = useCallback((row: Record<string, unknown>, action: 'view' | 'edit') => {
    if (getRowHref) return getRowHref(row, action);
    if (typeof window === 'undefined' || documentId) return undefined;
    if (action === 'edit' && onEditRow) return undefined;
    if (action === 'view' && onViewRow) return undefined;
    const id = String(row.id ?? row.documentId ?? '');
    if (!id) return undefined;   // sin id no hay destino: se queda como botón
    const url = new URL(window.location.href);
    url.searchParams.set(PARAM_DOCUMENTO, id);
    if (action === 'view') url.searchParams.set(PARAM_MODO, 'view');
    else url.searchParams.delete(PARAM_MODO);
    return `${url.pathname}${url.search}${url.hash}`;
  }, [getRowHref, documentId, onEditRow, onViewRow]);

  // ── Estado de la fila (DEF-4) ────────────────────────────
  // Resuelve el `docState` de una fila del listado. El campo es configurable vía
  // template.list.rowStateField; si no, prueba claves comunes.
  const rowStateField = listConfig?.rowStateField;
  const getRowState = useCallback((row: Record<string, unknown>): string => {
    if (rowStateField) return String(row[rowStateField] ?? '');
    return String(row.docState ?? row.state ?? row.estado ?? '');
  }, [rowStateField]);

  const getRowId = useCallback(
    (row: Record<string, unknown>) => String(row.id ?? row.documentId ?? ''),
    [],
  );

  // ── Acciones disponibles por estado (DEF-4) ──────────────
  // getAvailableActions(documentId, docState) es la fuente de verdad de qué
  // acciones (delete/cancel) ofrece cada fila. El resultado depende del estado,
  // así que se cachea por docState: una página de N filas con M estados ⇒ M
  // llamadas, no N. Si el adapter no lo implementa, no hay gateo por estado y se
  // mantiene el comportamiento legacy vía onDeleteRow.
  const [availableByState, setAvailableByState] = useState<Record<string, string[]>>({});

  const distinctRowStates = useMemo(
    () => [...new Set(rows.map(getRowState))].filter(Boolean).sort().join(','),
    [rows, getRowState],
  );

  useEffect(() => {
    if (!adapter.getAvailableActions) return;
    const states = distinctRowStates ? distinctRowStates.split(',') : [];
    if (states.length === 0) {
      setAvailableByState({});
      return;
    }
    let cancelled = false;
    Promise.all(
      states.map(async (st) => {
        const repRow = rows.find((r) => getRowState(r) === st);
        const repId = repRow ? getRowId(repRow) : '';
        try {
          const keys = await adapter.getAvailableActions!(repId, st);
          return [st, keys] as const;
        } catch {
          return [st, [] as string[]] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled) setAvailableByState(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [distinctRowStates, adapter, rows, getRowState, getRowId]);

  // Resuelve un ícono del template (string) a ReactNode vía el iconRegistry.
  const resolveIcon = useCallback(
    (iconName?: string): ReactNode => {
      if (!iconName) return undefined;
      const Icon = iconRegistry[iconName];
      return Icon ? <Icon className="w-4 h-4" /> : undefined;
    },
    [iconRegistry],
  );

  // ── Token de concurrencia de la fila ─────────────────────
  // Campo de la fila que transporta el row_version de la cabecera (configurable
  // vía template.list.concurrencyTokenField; por defecto `rowVersion`). Clave del
  // header en el DocumentState: la misma del detalle (template.versionKeys.header,
  // por defecto `_rowVersion`).
  const concurrencyTokenField = listConfig?.concurrencyTokenField ?? 'rowVersion';
  const headerVersionKey = template.versionKeys?.header ?? '_rowVersion';

  // Lee el token de concurrencia de una fila. Devuelve undefined cuando la fila
  // no expone el campo (NO se fuerza 0): así el adapter distingue "no hay token"
  // de "token = 0" y se preserva la retrocompatibilidad.
  const getRowVersion = useCallback((row: Record<string, unknown>): string | undefined => {
    const raw = row[concurrencyTokenField];
    return raw == null ? undefined : String(raw);
  }, [concurrencyTokenField]);

  // Construye un DocumentState mínimo para ejecutar la acción vía el adapter,
  // preservando documentId y docState reales de la fila. Cuando la fila trae el
  // token de concurrencia, lo propaga en `serverVersion` y en
  // `headerValues[headerVersionKey]` (las mismas claves que el flujo de detalle),
  // para que el backend detecte conflictos de edición concurrente (409).
  const buildRowState = useCallback((
    id: string,
    docState: string,
    serverVersion?: string,
  ): DocumentState => ({
    documentId: id,
    mode: 'edit',
    docState,
    headerValues: serverVersion != null ? { [headerVersionKey]: serverVersion } : {},
    lines: [],
    headerErrors: {},
    lineErrors: {},
    loading: false,
    saving: false,
    serverVersion,
    factBoxData: {},
  }), [headerVersionKey]);

  // Ejecuta una acción de fila vía el adapter, igual que el action bar del
  // detalle. DEF-3: el error ya NO se traga — se propaga como feedback (toast).
  const runRowAction = useCallback(async (
    actionKey: string,
    id: string,
    docState: string,
    serverVersion?: string,
  ) => {
    if (!id) return;
    markRowBusy(id, true);
    try {
      if (actionKey === 'delete' && adapter.deleteDocument) {
        await adapter.deleteDocument(id);
      } else {
        const result = await adapter.executeAction(actionKey, buildRowState(id, docState, serverVersion));
        if (result && result.success === false) {
          // El `message` del adapter YA es texto redactado para el usuario. NO se envuelve en un
          // `Error` para que lo reclasifique el `catch` de abajo: `classifyError` toma el `message`
          // de un `Error` por texto técnico de JS y lo sustituye por el genérico, así que ese rodeo
          // le mostraba "Ocurrió un error inesperado" en lugar del motivo real del rechazo. Mismo
          // criterio que `handleAction` del reducer, que ya despacha `result.message` directo.
          toast.error(t('master_document.chrome.error'), {
            description: result.message || t('master_document.error.actionFailed'),
          });
          return;
        }
      }
      fetchList(page, activePill, searchValues);
    } catch (err) {
      // El texto técnico de JS no se le muestra al usuario: va al log. La
      // descripción del toast sale del backend o de una clave del catálogo.
      const { message, technical } = classifyError(err, t);
      if (technical) console.error('[MasterDocument] acción de fila falló:', technical);
      toast.error(t('master_document.chrome.error'), { description: message });
    } finally {
      // En `finally` y no al final del `try`: el rechazo del adapter
      // (`result.success === false`) sale por un `return` temprano, y sin esto
      // esa fila se quedaría girando para siempre tras un toast de error.
      markRowBusy(id, false);
    }
  }, [adapter, buildRowState, fetchList, page, activePill, searchValues, t, markRowBusy]);

  // ── Row actions (DEF-4) ──────────────────────────────────
  // Por fila, devuelve las acciones gateadas por el estado de esa fila. Reutiliza
  // el actionRegistry para label/variant/ícono/confirmación; delete y cancel caen
  // a defaults sensatos cuando el registry no los define.
  const getRowActions = useCallback((row: Record<string, unknown>): RowAction[] => {
    const st = getRowState(row);
    const id = getRowId(row);
    const rowVersion = getRowVersion(row);
    const keys = availableByState[st] ?? [];
    const registry = template.actionRegistry ?? {};
    const actions: RowAction[] = [];

    if (keys.includes('delete')) {
      const entry = registry.delete;
      actions.push({
        key: 'delete',
        label: entry?.label ?? t('master_document.list.action.delete'),
        icon: resolveIcon(entry?.icon),
        variant: entry?.variant ?? 'danger',
        requiresConfirmation: entry?.requiresConfirmation ?? true,
        confirmationMessage: entry?.confirmationMessage
          ?? t('master_document.document.deleteConfirm'),
        onClick: () => { void runRowAction('delete', id, st, rowVersion); },
      });
    }

    if (keys.includes('cancel')) {
      const entry = registry.cancel;
      actions.push({
        key: 'cancel',
        label: entry?.label ?? t('master_document.list.action.void'),
        icon: resolveIcon(entry?.icon),
        variant: entry?.variant ?? 'danger',
        // El adapter puede abrir su propio modal de motivo, por eso cancel no
        // exige confirmación por defecto (sí la respeta si el registry la declara).
        requiresConfirmation: entry?.requiresConfirmation ?? false,
        confirmationMessage: entry?.confirmationMessage,
        onClick: () => { void runRowAction('cancel', id, st, rowVersion); },
      });
    }

    return actions;
  }, [getRowState, getRowId, getRowVersion, availableByState, template.actionRegistry, resolveIcon, runRowAction, t]);

  // ── Impresión nativa por fila ────────────────────────────
  // Construye el PrintContext de una fila y delega en la API del provider
  // (openPrint / isPrintAvailable). NO pasa por el adapter: es presentación.
  const buildRowPrintContext = useCallback((row: Record<string, unknown>): PrintContext => ({
    source: 'row',
    documentId: getRowId(row),
    documentNumber: (row._documentNumber as string | undefined) ?? null,
    docState: getRowState(row) || null,
    row,
  }), [getRowId, getRowState]);

  const isPrintRowAvailable = useCallback(
    (row: Record<string, unknown>) => print?.isPrintAvailable(buildRowPrintContext(row)) ?? false,
    [print, buildRowPrintContext],
  );

  const onPrintRow = useCallback(
    (row: Record<string, unknown>) => print?.openPrint(buildRowPrintContext(row)),
    [print, buildRowPrintContext],
  );

  // Legacy (retrocompat): borrado gobernado por flag global, sólo cuando el
  // adapter NO implementa getAvailableActions. Antes tragaba el error (DEF-3);
  // ahora lo propaga como feedback.
  // Documento pendiente de confirmar el borrado (null = sin diálogo abierto). Antes se preguntaba
  // con un `window.confirm` nativo; ahora usa el `ConfirmDialog` del kit, igual que las acciones de
  // fila de `DocumentList` y que el borrado de línea de `SmartMasterDocument`.
  // Se guarda la FILA completa, no solo su id: la rama legacy (sin `deleteDocument`) arma el estado
  // con `getRowState(row)` / `getRowVersion(row)` al confirmar.
  const [pendingDeleteRow, setPendingDeleteRow] = useState<Record<string, unknown> | null>(null);

  const handleDeleteRow = useCallback((row: Record<string, unknown>) => {
    if (!getRowId(row)) return;
    setPendingDeleteRow(row);
  }, [getRowId]);

  const confirmDeleteRow = useCallback(async () => {
    const row = pendingDeleteRow;
    if (!row) return;
    const id = getRowId(row);
    if (!id) return;
    setPendingDeleteRow(null);
    markRowBusy(id, true);
    try {
      if (adapter.deleteDocument) {
        await adapter.deleteDocument(id);
      } else {
        await adapter.executeAction('delete', buildRowState(id, getRowState(row), getRowVersion(row)));
      }
      fetchList(page, activePill, searchValues);
    } catch (err) {
      // El texto técnico de JS no se le muestra al usuario: va al log. La
      // descripción del toast sale del backend o de una clave del catálogo.
      const { message, technical } = classifyError(err, t);
      if (technical) console.error('[MasterDocument] acción de fila falló:', technical);
      toast.error(t('master_document.chrome.error'), { description: message });
    } finally {
      markRowBusy(id, false);
    }
  }, [adapter, buildRowState, pendingDeleteRow, getRowId, getRowState, getRowVersion, fetchList, page, activePill, searchValues, t, markRowBusy]);

  const handleBackToList = useCallback(() => {
    setView({ kind: 'list' });
  }, []);

  const adapterWithNav = useMemo(() => {
    const wrapped = { ...adapter };
    const origAction = adapter.executeAction;
    wrapped.executeAction = async (key, state) => {
      if (key === 'navigate_back') {
        handleBackToList();
        return { success: true };
      }
      if (key.startsWith('navigate_document:')) {
        const docId = key.replace('navigate_document:', '');
        if (docId) setView({ kind: 'document', documentId: docId });
        return { success: true };
      }
      return origAction(key, state);
    };
    return wrapped;
  }, [adapter, handleBackToList]);

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════

  if ((view.kind === 'list' || view.kind === 'wizard') && listConfig) {
    return (
      <>
        <DocumentList
          title={listConfig.title}
          subtitle={listConfig.subtitle}
          companies={availableCompanies}
          selectedCompany={selectedListCompany}
          onCompanyChange={handleListCompanyChange}
          onCreateNew={() => createFromSourceEnabled ? setCreateFromSourceOpen(true) : setView({ kind: 'wizard' })}
          createNewLabel={listConfig.createNewLabel}
          canCreate={canCreate}
          pills={pills}
          onPillClick={(key) => { setActivePill(key); setPage(1); }}
          searchFields={searchFields}
          searchValues={searchValues}
          onSearchChange={(k, v) => setSearchValues((prev) => ({ ...prev, [k]: v }))}
          fetcher={adapter.fetcher}
          columnFilters={columnFilters}
          onColumnFilterChange={handleColumnFilterChange}
          onSearch={() => fetchList(1, activePill, searchValues)}
          onClearSearch={() => { setSearchValues({}); fetchList(1, activePill, {}); }}
          searchOpen={searchOpen}
          onToggleSearch={() => setSearchOpen((o) => !o)}
          columns={columns}
          rows={rows}
          totalCount={totalCount}
          {...(
            // Fila-enlace y «Ver» son OPT-IN del host, no default del kit. Resueltos
            // aquí dentro llevaban al MISMO detalle que «Editar» —el `viewOnly`
            // interno cambia el modo, no la puerta—, así que el listado ofrecía dos
            // accesos indistinguibles y la fila entera era un enlace. Cuando el host
            // SÍ declara los suyos son destinos distintos de verdad (despachos y
            // notas crédito tienen `/$id` de sólo lectura aparte de `/$id/edit`), y
            // entonces se pasan tal cual.
            onRowClick ? { onRowClick: handleRowClick } : {}
          )}
          {...(onViewRow ? { onViewRow: handleViewRow } : {})}
          onEditRow={handleEditRow}
          getRowHref={resolverHrefDeFila}
          {...(adapter.getAvailableActions
            ? { getRowActions }
            : { onDeleteRow: handleDeleteRow })}
          {...(print
            ? { onPrintRow, isPrintRowAvailable, printLabel: print.label }
            : {})}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          // Cambiar el tamaño de página vuelve a la 1: quedarse en la página N
          // con un tamaño nuevo apunta a otros registros y se lee como que el
          // listado «saltó» solo.
          onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          quickSearchEnabled={quickSearchEnabled}
          quickSearchPlaceholder={quickSearchCfg?.placeholder}
          quickSearchValue={quickSearch}
          // Teclear devuelve a la primera página: el resultado filtrado tiene
          // menos páginas y quedarse en la 7 mostraría un listado vacío.
          onQuickSearchChange={(v) => { setQuickSearch(v); setPage(1); }}
          loading={listLoading}
          busyRowIds={busyRowIds}
          error={listError}
          onRetry={() => fetchList(page, activePill, searchValues)}
          bulkMode={!!(template.crossing?.enabled && template.crossing?.allowBulkConvert)}
          selectedRowIds={selectedRowIds}
          onRowSelect={(id, selected) =>
            setSelectedRowIds((prev) =>
              selected ? [...prev.filter((x) => x !== id), id] : prev.filter((x) => x !== id)
            )
          }
          bulkConvertOptions={bulkConvertOptions}
          onBulkConvert={(classId, ids, headerValues) => { void startBulkJob(classId, ids, headerValues); }}
        />

        <DocumentCreationWizard
          open={view.kind === 'wizard'}
          onClose={() => setView({ kind: 'list' })}
          companies={companies}
          selectedCompany={wizardCompanyId}
          onSelectCompany={handleWizardCompanyChange}
          documentClasses={documentClasses}
          selectedDocumentClass={wizardDocClass}
          onSelectDocumentClass={handleWizardClassChange}
          operationCenters={operationCenters}
          selectedCO={wizardCOId}
          onSelectCO={handleWizardCOChange}
          documentTypes={documentTypes}
          selectedType={selectedType}
          onSelectType={setSelectedType}
          onConfirm={handleWizardConfirm}
          loadingStep={
            // Las clases llegan por prop, no por fetch: el paso 2 nunca está en
            // carga cuando está activo. Los índices de CO y tipo se corren con él.
            companiesLoading ? 1
            : cosLoading ? coStepIndex
            : (typesLoading || confirmLoading) ? typeStepIndex
            : undefined
          }
        />

        {template.createFromSource && (
          <CreateFromSourceModal
            open={createFromSourceOpen}
            mode="create"
            config={template.createFromSource}
            adapter={adapter}
            operationCenterId={operationCenterId}
            userPermissions={userPermissions}
            formatCurrency={adapter.formatCurrency}
            formatNumber={adapter.formatNumber}
            onClose={() => setCreateFromSourceOpen(false)}
            onCreated={(newId) => {
              setCreateFromSourceOpen(false);
              if (onDocumentCreated) {
                onDocumentCreated(newId);
                return;
              }
              setView({ kind: 'document', documentId: newId });
            }}
            onFreeCapture={() => {
              // Captura libre (P25): en el kit equivale a crear un documento en
              // blanco vía el wizard. El consumidor puede enrutar a su propia
              // ruta (p.ej. /new?direct=true) inyectando su propio handler.
              setCreateFromSourceOpen(false);
              setView({ kind: 'wizard' });
            }}
          />
        )}

        {bulkJob && (
          <BulkConversionProgress
            job={bulkJob}
            onCancel={() => { void cancelBulkJob(); }}
            onClose={closeBulkProgress}
            onViewResults={openBulkResults}
          />
        )}
        <BulkResultsSummary
          open={showBulkResults}
          results={bulkJob?.results ?? []}
          onRetryFailed={(ids) => { void retryBulkFailed(ids); }}
          onClose={closeBulkResults}
        />
        {pendingDeleteRow !== null && (
          <ConfirmDialog
            message={t('master_document.document.deleteConfirm')}
            onConfirm={confirmDeleteRow}
            onCancel={() => setPendingDeleteRow(null)}
          />
        )}
      </>
    );
  }

  if (view.kind === 'new') {
    return <SmartMasterDocument initialState={view.initialState} onSaved={handleBackToList} onNavigateToList={handleBackToList} adapterOverride={adapterWithNav} onNavigateToDocument={onNavigateToDocument} />;
  }

  const docId = view.kind === 'document' ? view.documentId : undefined;
  const docViewOnly = view.kind === 'document' ? view.viewOnly : undefined;
  return <SmartMasterDocument documentId={docId} viewOnly={docViewOnly} onSaved={handleBackToList} onNavigateToList={handleBackToList} adapterOverride={adapterWithNav} onNavigateToDocument={onNavigateToDocument} />;
}
